/**
 * Actycrea: drop.esm.js (ES6)
 * MIT
 */
import { PREFIX } from '../helpers/config.js';
import { onResizeInit } from '../helpers/events.js';
import { getCurrentBreakpoint } from '../helpers/breakpoints.js';

const SELECTOR      = `data-${PREFIX}-mydr`;
const SELECTOR_DATA = `[${SELECTOR}]`;
const DATA_ITEMS_KEY = 'items';

const CLASS_ROOT            = `drop`;
const CLASS_PANEL           = `${CLASS_ROOT}-panel`;
const CLASS_OPEN            = `${CLASS_ROOT}-open`;
const CLASS_OUT             = `${CLASS_ROOT}-out`;

const DEFAULTS = {
  offsetY         : 20 ,
  closeOnNav      : false ,
  closeOnEsc      : true ,
  closeOnOutside  : true ,
  label           : '' ,
  removeDelay     : 500 ,
  scrollDeltaToHide : 100 ,
};


export class MyDR {
  /**
   * @param {HTMLElement} element  Botón disparador con [data-PREFIX-drop]
   * @param {Object}      config   Config opcional
   */
  constructor( element , config = {} ){
    if ( !( element instanceof HTMLElement ) ) throw new Error( 'MyDR: element inválido' );

    this._config  = { ...DEFAULTS , ...config };
    this._element = element;

    this._panel   = null;
    this._removeTO = null;
    this._items   = this._parseItems( element );

    // ARIA del disparador
    this._element.setAttribute( 'aria-haspopup' , 'menu' );
    this._element.setAttribute( 'aria-expanded' , 'false' );

    // binds
    this._onBtnClick = this._onBtnClick.bind( this );
    this._onDocClick = this._onDocClick.bind( this );
    this._onKeyDown  = this._onKeyDown.bind( this );
    this._onResize   = this._onResize.bind( this );
    this._onScroll   = this._onScroll.bind( this );

    // listeners
    this._element.addEventListener( 'click' , this._onBtnClick , false );
  }

  _parseItems( ){
    // dataset.items o atributo data-items
    let raw = this._element.dataset[ DATA_ITEMS_KEY ];
    if ( !raw ) raw = this._element.getAttribute( `data-${DATA_ITEMS_KEY}` );
    if ( !raw ) return [];
    try { return JSON.parse( raw ); }
    catch { return []; }
  }

  _buildPanel( ){
    if ( this._panel ) return;

    const panel = document.createElement( 'nav' );
    panel.className = CLASS_PANEL;
    panel.setAttribute( 'role' , 'menu' );
    panel.setAttribute( 'tabindex' , '-1' );
    panel.setAttribute( 'aria-label' , this._config.label );

    const spinner = document.createElement( 'div' );
    spinner.className = 'spinner';
    spinner.setAttribute( 'role' , 'status' );
    spinner.setAttribute( 'tabindex' , '-1' );

    const spinnerBorder = document.createElement( 'div' );
    spinnerBorder.className = 'spinner-border';
    spinner.appendChild( spinnerBorder );

    const ul = document.createElement( 'ul' );
    ul.className = `${CLASS_PANEL}-list`;

    panel.appendChild( ul );
    panel.appendChild( spinner );

    this._items.forEach( item => {
      const li = document.createElement( 'li' );
      li.className = `${CLASS_PANEL}-item`;

      const a = document.createElement( 'a' );
      a.className = `${CLASS_PANEL}-link${ item?.active == true ? ' active' : '' }`;
      a.href = item.url || '#';
      a.setAttribute( 'role' , 'menuitemcheckbox' );
      a.setAttribute( 'aria-checked' , item?.active == true ? 'true' : 'false' );

      const chk = document.createElement( 'input' );
      chk.type = 'checkbox';
      chk.className = 'form-check-input';
      if ( item.active ) chk.checked = true;
      chk.tabIndex = -1; // foco en <a>

      const text = document.createElement( 'span' );
      text.className = `${CLASS_PANEL}-text`;
      text.textContent = item.name || '';

      a.prepend( chk );
      a.appendChild( text );
      li.appendChild( a );
      ul.appendChild( li );
    } );

    // delegación para navegación
    panel.addEventListener( 'click' , ev => {
      const link = ev.target.closest( 'a' );
      if ( !link ) return;

      if ( this._config.closeOnNav ) {
        this.close( );
      } else {
        this._panel.classList.add( 'select' );
        this._selectItem( link );
      }
    } , false );

    document.body.appendChild( panel );
    this._panel = panel;
  }

  _selectItem( link ){
    document.documentElement.classList.add( CLASS_OUT );
    this._unbindGlobals();
    // desactivar anterior
    const prev = this._panel.querySelector( `.${CLASS_PANEL}-link.active` );
    if ( prev ){
      prev.classList.remove( 'active' );
      prev.setAttribute( 'aria-checked' , 'false' );
      const prevChk = prev.querySelector( 'input[type="checkbox"]' );
      if ( prevChk ) prevChk.checked = false;
    }
    link.classList.add( 'active' );
    link.setAttribute( 'aria-checked' , 'true' );
    const chk = link.querySelector( 'input[type="checkbox"]' );
    if ( chk ) chk.checked = true;
  }

  _position( ){
    if( ! this._panel || [ 'xs' , 'sm' ].includes( getCurrentBreakpoint( ) ) ) return;

    const rect = this._element.getBoundingClientRect();
    const top  = window.scrollY + rect.bottom; // pequeño margen
    const right = window.innerWidth - ( rect.right + window.scrollX );

    this._panel.style.position  = 'absolute';
    this._panel.style.top       = `${ top + this._config.offsetY }px`;
    this._panel.style.right     = `${ right }px`;
    this._panel.style.left      = 'auto'; // fuerza alineación por derecha

    // si se sale por la izquierda, corrige
    const pRect = this._panel.getBoundingClientRect();
    if ( pRect.left < 0 ){
      const newRight = Math.max( 8 , right - Math.abs( pRect.left ) - 8 );
      this._panel.style.right = `${ newRight }px`;
    }
  }

  _bindGlobals( ){
    if ( this._config.closeOnOutside ) document.addEventListener( 'click' , this._onDocClick , true );
    if ( this._config.closeOnEsc ) document.addEventListener( 'keydown' , this._onKeyDown , false );
    window.addEventListener( onResizeInit , this._onResize , { passive : true } );
    window.addEventListener( 'scroll' , this._onScroll , { passive : true } );
  }

  _unbindGlobals( ){
    document.removeEventListener( 'click' , this._onDocClick , true );
    document.removeEventListener( 'keydown' , this._onKeyDown , false );
    window.removeEventListener( onResizeInit , this._onResize );
    window.removeEventListener( 'scroll' , this._onScroll );
  }

  open( ){
    this._openScrollY = window.scrollY;
    if ( this._removeTO ){ clearTimeout( this._removeTO ); this._removeTO = null; }
    this._buildPanel();
    this._position( );
    this._panel.classList.add( 'show' );
    document.documentElement.classList.add( CLASS_OPEN );
    this._element.setAttribute( 'aria-expanded' , 'true' );
    this._bindGlobals();
    this._panel.focus();
  }

  close( ){
    if ( !this._panel ) return;
    this._panel.classList.remove( 'show' );
    document.documentElement.classList.remove( CLASS_OPEN );
    this._element.setAttribute( 'aria-expanded' , 'false' );
    this._unbindGlobals();

    if ( this._removeTO ){ clearTimeout( this._removeTO ); }
    const node = this._panel;
    this._removeTO = setTimeout( () => {
      if ( node && node.parentNode ){ node.parentNode.removeChild( node ); }
      this._panel = null;
      this._removeTO = null;
    }, Math.max( 0 , Number( this._config.removeDelay ) || 0 ) );
  }

  toggle( ){ ( this._panel && this._panel.classList.contains( 'show' ) ) ? this.close( ) : this.open( ); }

  // handlers
  _onBtnClick( ev ){
    ev.preventDefault();
    this.toggle( );
  }

  _onDocClick( ev ){
    if ( ev.target === this._element ) return;
    if ( this._panel && this._panel.contains( ev.target ) ) return;
    const otherTrigger = ev.target.closest( SELECTOR_DATA );
    if ( otherTrigger && otherTrigger !== this._element ){ this.close(); return; }
    this.close();
  }

  _onKeyDown( ev ){
    console.log( ev );
    if( ev.key === 'Escape' ){ this.close(); }
  }

  _onResize(){ this.close( ); }

  _onScroll(){
    if ( !this._panel ||  [ 'xs' , 'sm' ].includes( getCurrentBreakpoint( ) ) ) return;

    const delta = Math.abs( window.scrollY - ( this._openScrollY || 0 ) );
    const threshold = Number( this._config.scrollDeltaToHide ) || 0;

    // Si supera el umbral, cerrar en cualquier breakpoint
    if ( delta > threshold ){
      this.close();
      return;
    }

  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( el => {
      if( el._myhdInstance ) return;
      let cfg = { };
      const raw = el.getAttribute( SELECTOR );
      if( raw ){
        try { cfg = JSON.parse( raw ); } catch( _ ){ cfg = { }; }
      }
      el._myhdInstance = new MyDR( el , cfg );
      instances.push( el._myhdInstance );
    } );
    return instances;
  }
}

export default MyDR;
